import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { getLocalName, isEqual } from 'vet-bones/utils';

import { useRemoveRelationshipMapping } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveRelationshipMapping';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  Mapping,
  MappingIdentifier,
  MappingIdentifierPart,
  MappingIdentifierType,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import {
  isRelationshipMapping,
  isResource,
} from 'src/containers/ProjectDiagram/utils/data';
import { addRelationshipMapping } from 'src/containers/ProjectDiagram/utils/mapping/addRelationshipMapping';
import { setRelationshipMappingPrimaryIdentifier } from 'src/containers/ProjectDiagram/utils/mapping/setRelationshipMappingPrimaryIdentifier';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { getResourceValue } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { IRI_MATCHER } from 'src/containers/ProjectMapping/createNewIdentifierDelimiterItemRenderer';
import { DEFAULT_DELIMITER } from 'src/containers/ProjectMapping/hooks/compositePrimaryIdentifiers';
import { useMappingFromRoute } from 'src/containers/ProjectMapping/hooks/useMappingFromRoute';
import { ProjectMappingPrimaryIdentifierMultiSelect } from 'src/containers/ProjectMapping/ProjectMappingPrimaryIdentifierMultiSelect';
import {
  MultiSelectIdentifierItem,
  RelationshipMappingCell,
} from 'src/containers/ProjectMapping/types';
import { projectLinkDataArraySelector } from 'src/selectors/projectLinkDataArray';
import { projectNodeDataSelector } from 'src/selectors/projectNodeData';
import { projectResourceFieldItemsSelector } from 'src/selectors/projectResource';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type ProjectClassMappingRelationshipMappingCellPrimaryIdentifierProps = {
  classIri: string | null;
  currentIdentifier: MappingIdentifier;
  isSource: boolean;
  mappingId: string;
  relationshipIri: string;
  relationshipMappingKey: string | null;
  setCell: (partialCell: Partial<RelationshipMappingCell>) => void;
};

export const ProjectClassMappingRelationshipCellPrimaryIdentifier: React.VFC<
  ProjectClassMappingRelationshipMappingCellPrimaryIdentifierProps
> = ({
  classIri,
  currentIdentifier,
  isSource,
  mappingId,
  relationshipIri,
  relationshipMappingKey,
  setCell,
}) => {
  const { projectId } = useRouterParams();
  const diagram = useDiagram();
  const mappingForPage = useMappingFromRoute() as Mapping & { to: string };
  const classLocalName = classIri
    ? getLocalName(getModelConceptClassValue(classIri))
    : '';

  const removeRelationshipMapping = useRemoveRelationshipMapping();

  const resourceData = useReduxSelector(
    projectNodeDataSelector(projectId, mappingForPage.from, isResource),
    isEqual
  );
  const fieldItems = useReduxSelector(
    projectResourceFieldItemsSelector(
      projectId,
      getResourceValue(mappingForPage.from),
      resourceData?.type
    )
  ).map((field) => ({
    text: field.text,
    type: MappingIdentifierType.FIELD,
    value: field.value,
  }));
  const linkDataArray = useReduxSelector(
    projectLinkDataArraySelector(projectId),
    isEqual
  );

  const relationshipMapping = React.useMemo(
    () =>
      linkDataArray.find(
        (linkData): linkData is RelationshipMapping =>
          isRelationshipMapping(linkData) &&
          linkData.key === relationshipMappingKey
      ),
    [linkDataArray, relationshipMappingKey]
  );
  const relationshipMappingId = relationshipMapping?.value;

  const currentIdentifierItems: MultiSelectIdentifierItem[] =
    currentIdentifier.parts.map((identifier) => {
      const fieldItem = fieldItems.find(
        (item) => item.value === identifier.value
      );
      return {
        ...identifier,
        text: fieldItem?.text || identifier.value,
      };
    });

  const handleAddPart = React.useCallback(
    (item: MultiSelectIdentifierItem) => {
      if (!diagram) {
        return;
      }

      // trim whitespace from delimiters when adding a part
      const valueToAdd =
        item.type === MappingIdentifierType.DELIMITER
          ? item.value.trim()
          : item.value;

      // check for special characters in the identifier and do not add them
      if (
        !valueToAdd.match(IRI_MATCHER) &&
        item.type === MappingIdentifierType.DELIMITER
      ) {
        return;
      }

      const additions: MappingIdentifierPart[] = [];
      if (
        item.type === MappingIdentifierType.FIELD &&
        currentIdentifier.parts.length > 0 &&
        currentIdentifier.parts[currentIdentifier.parts.length - 1].type ===
          MappingIdentifierType.FIELD
      ) {
        additions.push({
          type: MappingIdentifierType.DELIMITER,
          value: DEFAULT_DELIMITER,
        });
      }
      additions.push({
        type: item.type,
        value: valueToAdd,
      });

      const newIdentifier = {
        ...currentIdentifier,
        parts: [...currentIdentifier.parts, ...additions],
      };

      if (classIri && !currentIdentifier.parts.length) {
        commitDiagramModel(diagram, (model) => {
          const newRelMapping = isSource
            ? addRelationshipMapping({
                fromClassIri: classIri,
                fromPrimaryIdentifier: newIdentifier,
                relationshipIri,
                to: mappingForPage.key,
                value: mappingId,
              })(model)
            : addRelationshipMapping({
                from: mappingForPage.key,
                relationshipIri,
                toClassIri: classIri,
                toPrimaryIdentifier: newIdentifier,
                value: mappingId,
              })(model);

          setCell({
            classMappingIdentifier: newIdentifier,
            mappingId: newRelMapping.value,
            relationshipMappingKey: newRelMapping.key,
          });
        });
      } else if (relationshipMappingKey) {
        commitDiagramModel(
          diagram,
          setRelationshipMappingPrimaryIdentifier({
            key: relationshipMappingKey,
            [isSource ? 'fromPrimaryIdentifier' : 'toPrimaryIdentifier']:
              newIdentifier,
          })
        );
        setCell({ classMappingIdentifier: newIdentifier });
      }
    },
    [
      classIri,
      currentIdentifier,
      diagram,
      isSource,
      mappingForPage,
      mappingId,
      relationshipIri,
      relationshipMappingKey,
      setCell,
    ]
  );
  const handleRemovePart = React.useCallback(
    (_item: MultiSelectIdentifierItem, index: number) => {
      const updatedParts = [...currentIdentifier.parts];
      updatedParts.splice(index, 1);
      const newIdentifier = {
        ...currentIdentifier,
        parts: updatedParts,
      };

      // if last part of identifier is removed, remove
      // relationship mapping and class mapping

      if (!newIdentifier.parts.length) {
        if (relationshipMappingKey) {
          removeRelationshipMapping(relationshipMappingKey);
        }
      } else if (diagram && relationshipMappingKey) {
        commitDiagramModel(
          diagram,
          setRelationshipMappingPrimaryIdentifier({
            key: relationshipMappingKey,
            [isSource ? 'fromPrimaryIdentifier' : 'toPrimaryIdentifier']:
              newIdentifier,
          })
        );
      }

      setCell({
        classMappingIdentifier: newIdentifier,
        mappingId: newIdentifier.parts.length ? relationshipMappingId : uuid(),
        relationshipMappingKey: newIdentifier.parts.length
          ? relationshipMappingKey
          : null,
      });
    },
    [
      currentIdentifier,
      diagram,
      isSource,
      relationshipMappingId,
      relationshipMappingKey,
      removeRelationshipMapping,
      setCell,
    ]
  );
  const handleUpdateIri = React.useCallback(
    (updatedBaseIri: string | null) => {
      const newIdentifier = {
        ...currentIdentifier,
        baseIri: updatedBaseIri,
      };

      if (diagram && relationshipMappingKey) {
        const relationshipMapping = diagram.model.linkDataArray.find(
          (linkData): linkData is RelationshipMapping =>
            isRelationshipMapping(linkData) &&
            linkData.key === relationshipMappingKey
        );

        if (relationshipMapping) {
          commitDiagramModel(
            diagram,
            setRelationshipMappingPrimaryIdentifier({
              key: relationshipMappingKey,
              [isSource ? 'fromPrimaryIdentifier' : 'toPrimaryIdentifier']:
                newIdentifier,
            })
          );
        }
      }

      setCell({ classMappingIdentifier: newIdentifier });
    },
    [currentIdentifier, diagram, isSource, relationshipMappingKey, setCell]
  );

  return (
    <ProjectMappingPrimaryIdentifierMultiSelect
      allowIri
      classLocalName={classLocalName}
      currentIdentifier={currentIdentifier}
      currentItems={currentIdentifierItems}
      fieldItems={fieldItems}
      onRemove={handleRemovePart}
      onSelect={handleAddPart}
      onUpdateIri={handleUpdateIri}
      resourceData={resourceData}
    />
  );
};
